import React, { useEffect} from 'react';
import { useSelector } from 'react-redux';
import { Col, Row, Spinner } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import { AutoSizer, Column, Table } from 'react-virtualized';
import { FaCircle } from '../../../../node_modules/react-icons/fa';
import Title from '../../../sharedComponents/Title/Title';
import useDashboard from '../useDashboard';
import formatters from '../../../config/formatters';
import { useState } from 'react';
import useDataSuite from '../../DataSuite/useDataSuite';
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import { isMobileOnly } from 'react-device-detect';
import { Theme } from '../../../config/constants';
import SelectAllSearch from '../../../sharedComponents/Select/SelectAllSearch';

ChartJS.register(...registerables);

const CompetitiveInsightsChart = () => {
    const competitiveInsightsTableData = useSelector(({dashboard}) => dashboard.competitiveInsightsData);
    const allFilters = useSelector(({datasuite}) => datasuite.allFilters);
    const topCategories = useSelector(({dashboard}) => dashboard.topCategories);
    const [selectedFilters, setSelectedFilters] = useState([]);
    const isLoading = useSelector(({dashboard}) => dashboard.competitiveInsightsChartIsLoading);
    const competitiveInsightsTableLabels = ['Your Business', 'Competitor', 'Competitor', 'Competitor', 'Competitor'];
    const tableColors = [Theme().colors.accent, 'rgba(49, 65, 117)', 'rgba(49, 65, 117)', 'rgba(49, 65, 117)', 'rgba(49, 65, 117)'];
    const [tableVolumeData, setTableVolumeData] = useState([]);
    const [tablePercentData, setTablePercentData] = useState([]);
    const { getCompetitiveInsightsChartData } = useDashboard.useDates();
    const { getFilters } = useDataSuite.useDataSuite();
    const { percentFormatter, usCurrencyFormatter } = formatters();

    // eslint-disable-next-line
    useEffect(() => {setupDataTables()}, [competitiveInsightsTableData, isLoading]);

    // eslint-disable-next-line
    useEffect(() => {getFilters()}, []);

    // eslint-disable-next-line
    useEffect(() => {getCompetitiveInsightsChartData(selectedFilters)}, [selectedFilters, topCategories]);

    const categoriesOptionsMap = allFilters?.categories?.map((data) => {
        return (
            {value: data, label: data}
        );
    });
    
    const setupDataTables = () => {
        if(!isLoading && competitiveInsightsTableData?.manufacturer && competitiveInsightsTableData?.competitors) {
            const yourBusinessData = competitiveInsightsTableData?.manufacturer ?? [];
            const competitiveInsightsData = competitiveInsightsTableData?.competitors ?? [];
            if (yourBusinessData?.length > 0 && competitiveInsightsData?.length > 0) {
                setTablePercentData([yourBusinessData?.[0]?.sales_percent, competitiveInsightsData?.[0]?.sales_percent, competitiveInsightsData?.[1]?.sales_percent, competitiveInsightsData?.[2]?.sales_percent, competitiveInsightsData?.[3]?.sales_percent]);
                setTableVolumeData([yourBusinessData?.[0]?.total_volume, competitiveInsightsData?.[0]?.total_volume, competitiveInsightsData?.[1]?.total_volume, competitiveInsightsData?.[2]?.total_volume, competitiveInsightsData?.[3]?.total_volume]);    
                var competitiveInsightsTableCanvas = document.getElementById("competitiveInsightsTableCanvas");
                var competitiveInsightsChart = ChartJS.getChart("competitiveInsightsTableCanvas");
                if(competitiveInsightsChart !== undefined) {
                    competitiveInsightsChart.destroy();
                }
                // eslint-disable-next-line
                var competitiveInsightsTable = new ChartJS( competitiveInsightsTableCanvas ,{
                    type: 'bar',
                    data: {
                        labels: competitiveInsightsTableLabels, 
                        datasets: [
                            {
                                data: [yourBusinessData?.[0]?.sales_percent*100, competitiveInsightsData?.[0]?.sales_percent*100, competitiveInsightsData?.[1]?.sales_percent*100, competitiveInsightsData?.[2]?.sales_percent*100 , competitiveInsightsData?.[3]?.sales_percent*100],
                                backgroundColor: tableColors
                            }
                        ]
                    },
                    options: tableOptions
                });
        }
        }
    };

    const tableOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: {
                    display:false
                },
                ticks: {
                    display: false
                }
            },
            y: {
                grid: {
                    display: false
                }
            }
        },
        plugins: {
          title: {
            display: false,
          },
          legend: {
              display: false,
          },
          tooltip: {
              enabled: true,
              xAlign: 'center',
              yAlign: 'center'
          },
        },
    };

    if(isLoading || (competitiveInsightsTableData?.competitors?.length <= 0)) {
        return (
            <Col style={{minHeight: '315px'}} className='w-100 h-100'>
                <Row className='pb-1'>
                    <Col>
                        <Title title='Competitive Insights' />
                    </Col>
                    <Col className='mt-3'>
                        <SelectAllSearch 
                            isMulti
                            name='category' 
                            options = {categoriesOptionsMap}
                            onChange = {(value) => setSelectedFilters(value)}
                            value = {selectedFilters}
                            placeholder='Filter By Category'
                        />
                    </Col>
                </Row>
                <Row className="mx-auto" style={{height:'70%'}}>
                    <Col className="mx-auto my-auto">
                        {isLoading &&
                        <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                        }
                        {!isLoading && (competitiveInsightsTableData?.competitors?.length <= 0) &&
                        <EmptyDataMessage />
                        }
                    </Col>
                </Row>
            </Col>
        );
    }
    else return (
        <Col>
            {isMobileOnly ? (
            <>
            <Row className='pb-1'>
                <Col>
                    <Title title='Competitive Insights' />
                </Col>
                <Col className='mt-3'>
                    <SelectAllSearch 
                        isMulti
                        name='category' 
                        options = {categoriesOptionsMap}
                        onChange = {(value) => setSelectedFilters(value)}
                        value = {selectedFilters}
                        placeholder='Filter'
                    />
                </Col>
            </Row>
            <Col className="w-100 mb-3 mx-0" style={{minHeight: '315px'}}>
                <Col className="col-12 col-xl-6 p-0">
                    <canvas id="competitiveInsightsTableCanvas"/>
                </Col>
                <Col className="col-12 col-xl-6 text-size-14 p-0" style={{marginTop: '4%'}}>
                    <AutoSizer>
                        {({width, height}) => (
                            <Table className="table totalOpportunitiesTable border-grayLight" id="totalOpportunitiesTable"
                                width={width}
                                height={150}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={tableVolumeData?.length}
                                rowGetter={({ index }) => tableVolumeData?.[index]}
                            >
                                <Column
                                label='Manufacturer'
                                dataKey='tableVolumeData'
                                width={width * 0.4}
                                cellRenderer={({rowIndex}) => (<span><FaCircle className='w-fc my-auto' style={{color: tableColors[rowIndex%5]}}/> {competitiveInsightsTableLabels?.[rowIndex%5]}</span>)}
                                />
                                <Column
                                width={width * 0.4}
                                label='Total Volumne'
                                dataKey='tableVolumeData'
                                cellRenderer={({rowIndex}) => (<span>{usCurrencyFormatter.format(tableVolumeData[rowIndex])}</span>)}
                                />
                                <Column
                                width={width * 0.2}
                                label='Sales %'
                                dataKey='tableVolumeData'
                                cellRenderer={({rowIndex}) => (<span>{percentFormatter.format(tablePercentData[rowIndex])}</span>)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            </Col>
            </>
          ):(
            <>
           <Row className='pb-1'>
                <Col>
                    <Title title='Competitive Insights' />
                </Col>
                <Col className='mt-3'>
                    <SelectAllSearch 
                        isMulti
                        name='category' 
                        options = {categoriesOptionsMap}
                        onChange = {(value) => setSelectedFilters(value)}
                        value = {selectedFilters}
                        placeholder='Filter By Category'
                    />
                </Col>
            </Row>
            <Row className="w-100 mb-3 mx-0" style={{minHeight: '315px'}}>
                <Col className="col-12 col-xl-6 p-0 col-md-6">
                    <canvas id="competitiveInsightsTableCanvas"/>
                </Col>
                <Col className="col-12 col-xl-6 col-md-6 text-size-14 p-0" style={{marginTop: '4%'}}>
                <AutoSizer>
                        {({width, height}) => (
                            <Table className="table totalOpportunitiesTable border-grayLight" id="totalOpportunitiesTable"
                                width={width}
                                height={height}
                                headerHeight={34.5}
                                rowHeight={38}
                                rowCount={tableVolumeData?.length}
                                rowGetter={({ index }) => tableVolumeData?.[index]}
                            >
                                <Column
                                label='Manufacturer'
                                dataKey='tableVolumeData'
                                width={width * 0.4}
                                cellRenderer={({rowIndex}) => (<span><FaCircle className='w-fc my-auto' style={{color: tableColors[rowIndex%5]}}/> {competitiveInsightsTableLabels[rowIndex%5]}</span>)}
                                />
                                <Column
                                width={width * 0.4}
                                label='Total Volumne'
                                dataKey='tableVolumeData'
                                cellRenderer={({rowIndex}) => (<span>{usCurrencyFormatter.format(tableVolumeData[rowIndex])}</span>)}
                                />
                                <Column
                                width={width * 0.2}
                                label='Sales %'
                                dataKey='tableVolumeData'
                                cellRenderer={({rowIndex}) => (<span>{percentFormatter.format(tablePercentData[rowIndex])}</span>)}
                                />
                            </Table>
                        )}
                    </AutoSizer>
                </Col>
            </Row>
          </>
          )}
           
        </Col>
    );
    
}

export default CompetitiveInsightsChart;